import React, { useMemo } from 'react';

import {
  Control,
  FieldValues,
  Path,
  useController,
  UseControllerProps,
} from 'react-hook-form';

import { Autocomplete, InputBaseProps, TextField, TextFieldProps } from '@mui/material';
import { Sport } from '../../../interfaces/Sport';
import { useAutocompleteOneValue } from '../autocomplete-components/hooks/useAutocompleteOneValue';
import { Select } from '../Select/Select';

type SelectSportPickersProps<
  T extends FieldValues,
  TSportFieldName extends Path<T>,
  TSportPositionFieldName extends Path<T>,
> = {
  control: Control<T>;
  sports: Sport[];
  sportFieldProps: UseControllerProps<T, TSportFieldName>;
  sportPositionFieldProps: UseControllerProps<T, TSportPositionFieldName>;
  InputProps?: InputBaseProps;
} & Pick<TextFieldProps, 'variant'>;

const SelectSportPickers = <
  T extends FieldValues,
  TSportFieldName extends Path<T>,
  TSportPositionFieldName extends Path<T>,
>({
  control,
  sports,
  sportFieldProps,
  sportPositionFieldProps,
  variant,
  InputProps,
}: SelectSportPickersProps<T, TSportFieldName, TSportPositionFieldName>) => {
  const {
    field: sportField,
    fieldState: { error: sportError },
  } = useController<any>({
    control,
    defaultValue: null,
    ...sportFieldProps,
  });

  const {
    field: sportPositionField,
    fieldState: { error: sportPositionError },
  } = useController<any>({
    control,
    defaultValue: '',
    ...sportPositionFieldProps,
  });

  const positionsList = useMemo(
    () => sports.find((sport) => sport.value === sportField.value)?.positions || [],
    [sportField.value, sports],
  );

  const { options: sportOptions, getOptionLabel: getSportOptionLabel } =
    useAutocompleteOneValue({
      userOptions: sports.map((sport) => ({
        value: sport.value,
        label: sport.label,
      })),
      valueKey: 'value',
      labelKey: 'label',
    });

  const onSportChange = (_: any, value: string) => {
    if (sportPositionField.value) {
      const newPositions = sports.find((sport) => sport.value === value)?.positions || [];

      const isPositionExist = newPositions.find(
        (item) => item.value === sportPositionField.value,
      );

      if (!isPositionExist) {
        sportPositionField.onChange('');
      }
    }

    sportField.onChange(value);
  };

  return (
    <>
      <Autocomplete
        {...sportField}
        tabIndex={-1}
        fullWidth
        size="small"
        disableClearable
        onChange={onSportChange}
        options={sportOptions}
        getOptionLabel={getSportOptionLabel}
        slotProps={{
          popper: {
            className: 'general-style variant-select',
            placement: 'bottom-start',
          },
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            helperText={sportError?.message}
            error={!!sportError}
            label="Sport"
            required={!!sportFieldProps.rules?.required}
            variant={variant}
            inputRef={sportField.ref}
            slotProps={{
              input: { ...params.InputProps, ...InputProps },
            }}
          />
        )}
      />
      {sportField.value && (
        <Select
          {...sportPositionField}
          options={positionsList}
          value={sportPositionField.value}
          onChange={sportPositionField.onChange}
          inputRef={sportPositionField.ref}
          label="Position"
          error={!!sportPositionError}
          required={!!sportPositionFieldProps.rules?.required}
          helperText={sportPositionError?.message}
          size="small"
          variant={variant}
          InputProps={InputProps}
        />
      )}
    </>
  );
};

export { SelectSportPickers };
